import { type TeamStatus, useFormatter, WithSkeleton } from '@capsicum/common';
import { Image, SkeletonItem, Text } from '@capsicum/common-web';
import { useWCPledgeActivityTabTexts } from '@capsicum/features/world-championship-pledge/shared';
import { type StaticImageData } from 'next/image';
import styled from 'styled-components';

export type WCPledgeUserUnstakedPledgeItemProps = {
	bonusAmount: string;
	chzAmount: string;
	teamFlag?: StaticImageData | string;
	teamName: string;
	teamStatus: TeamStatus;
};

export const WCPledgeUserUnstakedPledgeItemComponent = ({
	bonusAmount,
	chzAmount,
	teamFlag,
	teamName,
	teamStatus
}: WCPledgeUserUnstakedPledgeItemProps) => {
	const { yourChilizSectionTexts } = useWCPledgeActivityTabTexts();
	const { formatCrypto } = useFormatter();

	return (
		<S.PledgeItem data-testid={`wcpledge-user-unstaked-item-${teamName.toLowerCase()}`}>
			<S.PledgeItemLeftColumn>
				{teamFlag && <Image alt={`${teamName} flag`} height={20} src={teamFlag} width={20} />}
			</S.PledgeItemLeftColumn>
			<S.PledgeItemRightColumn>
				<S.CardRow>
					<S.TeamName variant="text2">
						{`${yourChilizSectionTexts.refundText} ${teamName} ${
							teamStatus === 'Failed'
								? yourChilizSectionTexts.refundReasonNotQualifiedText
								: yourChilizSectionTexts.refundReasonUnstakeText
						}`}
					</S.TeamName>
					<S.ChzAmount testID={`wcpledge-${teamName.toLowerCase()}-chz-pledged-amount`} variant="text2">
						{`${formatCrypto(chzAmount, 'CHZ')}`}
					</S.ChzAmount>
				</S.CardRow>
				<S.CardRow>
					<S.ChzAmount variant="text2">{yourChilizSectionTexts.bonusText}</S.ChzAmount>
					<S.UsdAmount
						data-testid={`wcpledge-${teamName.toLowerCase()}-fiat-pledged-amount`}
						variant="caption2"
					>
						{formatCrypto(bonusAmount, 'CHZ')}
					</S.UsdAmount>
				</S.CardRow>
			</S.PledgeItemRightColumn>
		</S.PledgeItem>
	);
};

export const WCPledgeUserPledgeItemSkeleton = () => (
	<S.PledgeItem>
		<S.PledgeItemLeftColumn>
			<SkeletonItem borderRadius={2} height={20} width={20} />
		</S.PledgeItemLeftColumn>
		<S.PledgeItemRightColumn>
			<S.CardRow>
				<SkeletonItem borderRadius={2} height={20} width="100%" />
			</S.CardRow>
		</S.PledgeItemRightColumn>
	</S.PledgeItem>
);

export const WCPledgeUserUnstakedPledgeItem = WithSkeleton(
	WCPledgeUserUnstakedPledgeItemComponent,
	WCPledgeUserPledgeItemSkeleton
);

export namespace S {
	export const PledgeItem = styled.div`
		display: flex;
		flex-direction: row;
		padding: ${({ theme }) => theme.spacings.xsmall} 0;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		width: 100%;
	`;

	export const PledgeItemLeftColumn = styled.div`
		flex-shrink: 0;
	`;

	export const PledgeItemRightColumn = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		flex: 1;
	`;

	export const CardRow = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: flex-end;
		gap: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const TeamName = styled(Text)`
		flex-shrink: 1;
		${({ theme }) => theme.fonts.text.text2};
	`;

	export const ChzAmount = styled(Text)`
		${({ theme }) => theme.fonts.text.text2};
		flex-shrink: 0;
	`;

	export const UsdAmount = styled(Text)`
		${({ theme }) => theme.fonts.text.caption2};
	`;
}
